import { useState, useEffect, useCallback } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { api, imageUrl } from "@/lib/api";
import { CalendarStar, Plus, LinkSimple, Check, QrCode, Images, Globe, SpinnerGap, Trash, Lock, LockOpen, X, Eye, EyeSlash } from "@phosphor-icons/react";
import { toast } from "sonner";

const eventUrl = (code) => `${window.location.origin}/a/${code}`;

function QrModal({ album, onClose }) {
  if (!album) return null;
  const url = album.short_url || eventUrl(album.code);
  const hasSplink = !!album.qr_url;
  const downloadQr = () => {
    if (hasSplink) {
      window.open(`${album.qr_url}?format=svg`, "_blank");
      return;
    }
    const canvas = document.querySelector("#event-qr-canvas canvas");
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = `${album.name.replace(/[^a-z0-9]+/gi, "-")}-qr.png`;
    a.click();
  };
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" onClick={onClose}>
      <div className="w-full max-w-sm rounded-2xl bg-white p-6 text-center" onClick={(e) => e.stopPropagation()} data-testid="event-qr-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-lg font-black">{album.name}</h3>
          <button onClick={onClose} className="grid h-8 w-8 place-items-center rounded-md hover:bg-zinc-100"><X size={18} weight="bold" /></button>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">Guests scan this to add their photos.</p>
        <div id="event-qr-canvas" className="mt-5 grid place-items-center rounded-xl border border-border p-5">
          {hasSplink
            ? <img src={album.qr_url} alt="QR code" width={220} height={220} className="bg-white" data-testid="event-splink-qr" />
            : <QRCodeCanvas value={url} size={220} level="M" includeMargin />}
        </div>
        <p className="mt-3 break-all font-mono text-xs text-zinc-500" data-testid="event-short-url">{url}</p>
        {hasSplink && <p className="mt-1 text-[10px] font-semibold uppercase tracking-widest text-emerald-600">Powered by splink.pro</p>}
        <button onClick={downloadQr} className="mt-4 w-full rounded-md bg-[#F27A54] py-2.5 text-sm font-bold text-white hover:bg-[#d9633f]">
          {hasSplink ? "Download QR (print SVG)" : "Download QR (PNG)"}
        </button>
      </div>
    </div>
  );
}

function ManagePanel({ album, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get(`/events/${album.id}`).then((r) => setData(r.data)).catch(() => setData({ photos: [] }));
  }, [album.id]);
  useEffect(() => { load(); }, [load]);

  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const publish = async () => {
    if (!selected.size) return;
    setBusy(true);
    try {
      const r = await api.post(`/events/${album.id}/publish`, { ids: [...selected], action: "publish" });
      if (r.data.count) toast.success(`Published ${r.data.count} photo${r.data.count === 1 ? "" : "s"} to the gallery`);
      if (r.data.skipped) toast.warning(`${r.data.skipped} skipped — still waiting for an AI title`);
      setSelected(new Set());
      load(); onChanged?.();
    } catch { toast.error("Publish failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-white" data-testid="event-manage">
      <div className="flex items-center justify-between border-b border-border px-5 py-4">
        <div>
          <p className="overline text-[10px] text-[#F27A54]">Event pool</p>
          <h2 className="font-display text-xl font-black">{album.name}</h2>
        </div>
        <button onClick={onClose} className="grid h-9 w-9 place-items-center rounded-md hover:bg-zinc-100"><X size={20} weight="bold" /></button>
      </div>
      <div className="flex flex-wrap items-center gap-3 border-b border-border bg-zinc-50 px-5 py-3">
        <span className="text-sm font-semibold text-zinc-700">{selected.size} selected</span>
        <button
          data-testid="event-publish-selected" onClick={publish} disabled={!selected.size || busy}
          className="ml-auto flex items-center gap-1.5 rounded-md bg-[#F27A54] px-4 py-2 text-sm font-bold text-white hover:bg-[#d9633f] disabled:opacity-50">
          <Globe size={16} weight="bold" /> {busy ? "Publishing…" : "Publish selected"}
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-5">
        {!data ? (
          <div className="grid place-items-center py-20"><SpinnerGap size={30} className="animate-spin text-[#F27A54]" weight="bold" /></div>
        ) : data.photos.length === 0 ? (
          <div className="grid place-items-center py-20 text-center text-muted-foreground">
            <Images size={40} weight="light" className="text-zinc-300" />
            <p className="mt-3 font-display text-lg font-bold text-zinc-700">No photos yet</p>
            <p className="text-sm">Share the link or QR — guest photos will appear here.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            {data.photos.map((p) => {
              const sel = selected.has(p.id);
              const published = p.status === "published";
              return (
                <button key={p.id} data-testid={`event-photo-${p.id}`} onClick={() => !published && toggle(p.id)}
                  className={`group relative block overflow-hidden rounded-lg border text-left ${sel ? "border-[#F27A54] ring-2 ring-[#F27A54]" : "border-border"}`}>
                  <img src={imageUrl(p.id, "display")} alt="" loading="lazy" className="aspect-square w-full object-cover bg-zinc-100" />
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 to-transparent p-2">
                    <p className="line-clamp-1 text-[11px] font-semibold text-white">{p.title || "Awaiting AI title…"}</p>
                    {p.submitter_display_name && <p className="text-[10px] text-white/70">by {p.submitter_display_name}</p>}
                  </div>
                  {published
                    ? <span className="absolute left-2 top-2 rounded-full bg-[#F27A54] px-2 py-0.5 text-[9px] font-bold text-white">PUBLISHED</span>
                    : sel && <span className="absolute right-2 top-2 grid h-6 w-6 place-items-center rounded-full bg-[#F27A54] text-white"><Check size={13} weight="bold" /></span>}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default function EventAlbums() {
  const [albums, setAlbums] = useState(null);
  const [name, setName] = useState("");
  const [requireLogin, setRequireLogin] = useState(false);
  const [password, setPassword] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState("");
  const [qrAlbum, setQrAlbum] = useState(null);
  const [manageAlbum, setManageAlbum] = useState(null);

  const load = useCallback(() => {
    api.get("/events").then((r) => setAlbums(r.data.albums)).catch(() => setAlbums([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!name.trim()) { toast.error("Give your event album a name"); return; }
    setCreating(true);
    try {
      await api.post("/events", {
        name: name.trim(), require_login: requireLogin,
        password: password || undefined,
        expires_at: expiresAt ? new Date(expiresAt).toISOString() : undefined,
      });
      toast.success("Event album created — share the link or QR with your guests");
      setName(""); setRequireLogin(false); setPassword(""); setExpiresAt(""); load();
    } catch { toast.error("Could not create album"); }
    finally { setCreating(false); }
  };

  const copyLink = async (a) => {
    const url = a.short_url || eventUrl(a.code);
    try { await navigator.clipboard.writeText(url); setCopied(a.code); setTimeout(() => setCopied(""), 2000); toast.success("Link copied"); } catch { /* */ }
  };
  const del = async (a) => {
    if (!window.confirm(`Delete the event album "${a.name}"? Photos already published stay in the gallery.`)) return;
    try { await api.delete(`/events/${a.id}`); toast.success("Album deleted"); load(); } catch { toast.error("Delete failed"); }
  };
  const toggleGallery = async (a) => {
    try {
      const r = await api.post(`/events/${a.id}/settings`, { public_gallery: !a.public_gallery });
      setAlbums((list) => list.map((x) => (x.id === a.id ? { ...x, public_gallery: r.data.public_gallery } : x)));
      toast.success(r.data.public_gallery ? "Guest gallery is now public — everyone can see the album" : "Guest gallery turned off");
    } catch { toast.error("Could not update gallery setting"); }
  };

  return (
    <div className="mx-auto max-w-[1200px]">
      <div className="flex items-center gap-2.5">
        <CalendarStar size={26} weight="fill" className="text-[#F27A54]" />
        <div>
          <p className="overline text-[#F27A54]">Event Albums</p>
          <h1 className="font-display text-3xl font-black tracking-tight">Crowd-sourced event photos</h1>
        </div>
      </div>
      <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
        Create a shared album, show the QR at your event, and let everyone add photos from their phones — no app needed.
        Guest photos land in a private pool; you publish the best as a set.
      </p>

      {/* Create */}
      <div className="mt-6 rounded-xl border border-border bg-white p-5" data-testid="event-create-card">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="flex-1">
            <label className="overline mb-1 block text-[10px] text-muted-foreground">Event / album name</label>
            <input data-testid="event-name-field" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Nature Walk — Silent Valley, June 2026"
              className="h-10 w-full rounded-md border border-border px-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-[#F27A54]" />
          </div>
          <label className="flex cursor-pointer items-center gap-2 pb-2.5 text-sm font-medium text-zinc-700">
            <input type="checkbox" data-testid="event-require-login" checked={requireLogin} onChange={(e) => setRequireLogin(e.target.checked)}
              className="h-4 w-4 rounded border-zinc-300 text-[#F27A54] focus:ring-[#F27A54]" />
            {requireLogin ? <Lock size={15} weight="bold" /> : <LockOpen size={15} weight="bold" />}
            Require sign-in
          </label>
          <button data-testid="event-create-btn" onClick={create} disabled={creating}
            className="flex items-center justify-center gap-1.5 rounded-md bg-[#F27A54] px-5 py-2.5 text-sm font-bold text-white hover:bg-[#d9633f] disabled:opacity-50">
            <Plus size={16} weight="bold" /> {creating ? "Creating…" : "Create album"}
          </button>
        </div>
        <div className="mt-3 grid gap-3 border-t border-border pt-3 sm:grid-cols-2">
          <div>
            <label className="overline mb-1 flex items-center gap-1 text-[10px] text-muted-foreground"><Lock size={11} weight="bold" /> Client password (optional)</label>
            <input data-testid="event-password-field" type="text" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="e.g. sharma-wedding — guests need this to open the link"
              className="h-10 w-full rounded-md border border-border px-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-[#F27A54]" />
          </div>
          <div>
            <label className="overline mb-1 block text-[10px] text-muted-foreground">Link expires (optional)</label>
            <input data-testid="event-expiry-field" type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)}
              className="h-10 w-full rounded-md border border-border px-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-[#F27A54]" />
          </div>
          <p className="text-[11px] text-muted-foreground sm:col-span-2">Password + expiry are enforced on the branded <b>splink.pro</b> share link — perfect for private wedding / event delivery.</p>
        </div>
      </div>

      {/* List */}
      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="event-list">
        {albums === null ? (
          <div className="col-span-full grid place-items-center py-16"><SpinnerGap size={28} className="animate-spin text-[#F27A54]" weight="bold" /></div>
        ) : albums.length === 0 ? (
          <div className="col-span-full rounded-xl border border-dashed border-border py-14 text-center text-muted-foreground">
            <CalendarStar size={40} weight="light" className="mx-auto text-zinc-300" />
            <p className="mt-3 font-display text-lg font-bold text-zinc-700">No event albums yet</p>
            <p className="text-sm">Create one above and share the QR at your next event.</p>
          </div>
        ) : albums.map((a) => (
          <div key={a.id} data-testid={`event-card-${a.code}`} className="rounded-xl border border-border bg-white p-4">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="font-display text-lg font-bold leading-tight">{a.name}</h3>
                <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
                  {a.require_login ? <span className="flex items-center gap-1"><Lock size={11} weight="bold" /> Sign-in required</span> : <span className="flex items-center gap-1"><LockOpen size={11} weight="bold" /> Open link</span>}
                  {a.protected && <span className="flex items-center gap-1 font-semibold text-[#2D6A4F]"><Lock size={11} weight="fill" /> Password</span>}
                  {a.expires_at && <span className="text-amber-600">expires {new Date(a.expires_at).toLocaleDateString()}</span>}
                </p>
              </div>
              <button onClick={() => del(a)} title="Delete album" className="grid h-7 w-7 place-items-center rounded-md text-zinc-400 hover:bg-red-50 hover:text-red-600"><Trash size={15} weight="bold" /></button>
            </div>
            <div className="mt-3 flex items-center gap-3 text-sm">
              <span className="flex items-center gap-1 font-semibold text-zinc-700"><Images size={15} weight="fill" className="text-zinc-400" /> {a.photo_count} in pool</span>
              <span className="flex items-center gap-1 font-semibold text-[#F27A54]"><Globe size={15} weight="fill" /> {a.published_count} live</span>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <button data-testid={`event-copy-${a.code}`} onClick={() => copyLink(a)}
                className="flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-zinc-700 hover:bg-zinc-50">
                {copied === a.code ? <><Check size={14} weight="bold" className="text-emerald-600" /> Copied</> : <><LinkSimple size={14} weight="bold" /> Copy link</>}
              </button>
              <button data-testid={`event-qr-${a.code}`} onClick={() => setQrAlbum(a)}
                className="flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-zinc-700 hover:bg-zinc-50">
                <QrCode size={14} weight="bold" /> QR code
              </button>
              <button data-testid={`event-manage-${a.code}`} onClick={() => setManageAlbum(a)}
                className="flex items-center gap-1.5 rounded-md bg-[#F27A54] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#d9633f]">
                <Images size={14} weight="bold" /> Manage &amp; publish
              </button>
            </div>
            <label className="mt-3 flex cursor-pointer items-center gap-2 border-t border-border pt-3 text-xs font-semibold text-zinc-600">
              <input type="checkbox" data-testid={`event-gallery-toggle-${a.code}`} checked={!!a.public_gallery} onChange={() => toggleGallery(a)}
                className="h-4 w-4 rounded border-zinc-300 text-[#F27A54] focus:ring-[#F27A54]" />
              {a.public_gallery ? <Eye size={14} weight="bold" className="text-[#F27A54]" /> : <EyeSlash size={14} weight="bold" />}
              Public guest gallery {a.public_gallery && <span className="text-[#F27A54]">· live</span>}
            </label>
          </div>
        ))}
      </div>

      <QrModal album={qrAlbum} onClose={() => setQrAlbum(null)} />
      {manageAlbum && <ManagePanel album={manageAlbum} onClose={() => setManageAlbum(null)} onChanged={load} />}
    </div>
  );
}
